import { faCommentAltEdit } from '@fortawesome/pro-regular-svg-icons'
import { flatten } from 'lodash'
import React, { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { QueryFunctionContext, useInfiniteQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { Column } from 'react-table'
import { Place } from '../../types/utils'
import { placePerPage } from '../../utils/config'

import EmptyContent from '../EmptyContent'
import ErrorPage from '../ErrorPage'
import Spinner from '../Spinner'
import Table from '../Table'
import TableSkeleton from '../TableSkeleton'
import placeService from '../../services/placeService'
import toast from 'react-hot-toast'
import { BuzzlUsersError } from '../../types/buzzl-user'

const Locations = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const { ref, inView } = useInView({
    rootMargin: '100px 0px 0px 0px',
  })

  const locations = useInfiniteQuery(
    ['customerLocations', customerId],
    (context: QueryFunctionContext) => {
      return placeService.getAll({
        first: placePerPage,
        skip: context.pageParam || 0,
        where: { OR: [{ customer: { id: customerId } }, { requestedClaimBy: { id: customerId } }] },
      })
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < placePerPage) return false
        return flatten(allPages).length
      },
      onError: (e) => {
        const error = e as BuzzlUsersError
        error.response.errors.map((er) =>
          toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
        )
      },
    }
  )

  const { hasNextPage, fetchNextPage } = locations

  const columns = React.useMemo<Column<Place>[]>(
    () => [
      {
        Header: 'Location',
        accessor: ({ name }) => (
          <span style={{ maxWidth: 137 }} className="block truncate">
            {name}
          </span>
        ),
      },
      {
        Header: 'Experiences',
        accessor: 'nbExperiences',
      },
      {
        Header: 'Address',
        accessor: ({ address }) => (
          <span style={{ maxWidth: 184 }} className="block truncate">
            {address}
          </span>
        ),
      },
      {
        Header: 'Rating',
        accessor: ({ averageEvaluation }) => {
          if (averageEvaluation === 'NEGATIVE') {
            return <span className="tag negative-tag">Negative</span>
          } else if (averageEvaluation === 'POSITIVE') {
            return <span className="tag positive-tag">Positive</span>
          } else {
            return <span className="tag alert-tag">Neutral</span>
          }
        },
      },
      {
        Header: 'Category',
        accessor: ({ categories }) => categories.map((c) => c.name).join(','),
      },
      {
        Header: 'Status',
        accessor: ({ claimStatus }) => {
          if (claimStatus === 'UNCLAIMED') {
            return <span className="tag bg-gray-100 text-gray-800">Unclaimed</span>
          } else if (claimStatus === 'PENDING') {
            return <span className="tag alert-tag">Pending</span>
          } else {
            return <span className="tag positive-tag">Claimed</span>
          }
        },
      },
      {
        Header: ' ',
        disableSortBy: true,
        accessor: ({ id }) => (
          <button
            onClick={() => {
              navigate(`/locations/${id}`)
            }}
            className="text-link"
          >
            View
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    const init = async () => {
      if (inView && hasNextPage) {
        await fetchNextPage()
      }
    }
    init()
  }, [inView, hasNextPage, fetchNextPage])

  const locationsData = flatten(locations.data?.pages)

  if (locations.isLoading)
    return <TableSkeleton headers={['Name', 'Email', 'Assigned Locations', 'Creation Date', ' ']} />

  if (locations.isError || !locationsData) {
    return (
      <ErrorPage
        title="Something went wrong!"
        text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
      />
    )
  }

  if (locationsData.length === 0) {
    return (
      <div className="mt-32 flex h-full items-center justify-center">
        <EmptyContent
          title="No Locations found."
          description="This Customer dont have any users, please assign one."
          icon={faCommentAltEdit}
        />
      </div>
    )
  }

  return (
    <div>
      <Table columns={columns} data={locationsData} />
      {hasNextPage ? (
        <div ref={ref} className="mt-4 flex flex-col items-center text-center">
          <Spinner className="h-8 w-8 text-gray-400" />
          <span className="text-gray-400">Loading more...</span>
        </div>
      ) : null}
    </div>
  )
}

export default Locations
